import { useState, useCallback } from 'react';
import Cropper, { Area } from 'react-easy-crop';
import { getCroppedImage } from '../utils/imageUtils';
import styles from './AvatarCropModal.module.css';

interface AvatarCropModalProps {
  /** Base64 Data-URL des Originalbildes */
  imageSrc: string;
  /** Callback mit dem zugeschnittenen Bild als Base64 Data-URL */
  onCrop: (croppedBase64: string) => void;
  /** Modal schließen ohne Crop */
  onClose: () => void;
}

export default function AvatarCropModal({ imageSrc, onCrop, onClose }: AvatarCropModalProps) {
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<Area | null>(null);
  const [saving, setSaving] = useState(false);

  const onCropComplete = useCallback((_croppedArea: Area, croppedPixels: Area) => {
    setCroppedAreaPixels(croppedPixels);
  }, []);

  const handleSave = async () => {
    if (!croppedAreaPixels) return;
    setSaving(true);
    try {
      const cropped = await getCroppedImage(imageSrc, croppedAreaPixels, 1200);
      onCrop(cropped);
    } catch (err) {
      console.error('Crop fehlgeschlagen:', err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className={styles.overlay} onClick={onClose}>
      <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
        <div className={styles.header}>
          <h3>Profilbild zuschneiden</h3>
          <button className={styles.closeBtn} onClick={onClose}>&times;</button>
        </div>

        <div className={styles.cropContainer}>
          <Cropper
            image={imageSrc}
            crop={crop}
            zoom={zoom}
            aspect={1}
            cropShape="round"
            showGrid={false}
            onCropChange={setCrop}
            onZoomChange={setZoom}
            onCropComplete={onCropComplete}
            minZoom={0.5}
            maxZoom={5}
          />
        </div>

        <div className={styles.controls}>
          <span className={styles.zoomLabel}>Kleiner</span>
          <input
            type="range"
            className={styles.zoomSlider}
            min={0.5}
            max={5}
            step={0.05}
            value={zoom}
            onChange={(e) => setZoom(Number(e.target.value))}
          />
          <span className={styles.zoomLabel}>Gr&ouml;&szlig;er</span>
        </div>

        <div className={styles.footer}>
          <button className={styles.cancelBtn} onClick={onClose}>
            Abbrechen
          </button>
          <button
            className={styles.saveBtn}
            onClick={handleSave}
            disabled={saving || !croppedAreaPixels}
          >
            {saving ? 'Wird zugeschnitten...' : 'Übernehmen'}
          </button>
        </div>
      </div>
    </div>
  );
}
